/**
 * Taste and leftovers (#1281).
 *
 * One food per card, four buttons, a progress bar, eater A then eater B, then
 * one leftovers question each.
 *
 * Three things this screen has to get right:
 *
 *   PRESETS FIRST. Sixteen cards is a swipe deck a person finishes, but it is
 *   still thirty-two taps for a household, and most of those answers have been
 *   true for years. A preset fills what it knows and drops you into the deck at
 *   the first card it did not answer.
 *
 *   SAVE AS YOU GO. Each answer is written immediately. A profile abandoned
 *   halfway is a normal state — the API merges rather than replaces, and the
 *   server reads an unanswered group as "will eat", never as a refusal.
 *
 *   LEFTOVERS ARE NOT A TASTE. Somebody can love salmon and refuse it cold at
 *   noon. It is asked separately, after the food, because it sets quantities
 *   rather than menus.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  twoPlates,
  AFFINITY_LABELS,
  LEFTOVER_LABELS,
  type Eater,
  type FoodGroup,
  type TastePreset,
} from "../api/twoPlates";

type Stage = "preset" | "cards" | "leftovers";

const SCORES = [3, 2, 1, 0];

function plateColour(index: number) {
  return index % 2 === 0 ? "var(--color-plate-a)" : "var(--color-plate-b)";
}

export default function Taste() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const groupsQuery = useQuery({ queryKey: ["food-groups"], queryFn: twoPlates.foodGroups });
  const householdQuery = useQuery({ queryKey: ["household"], queryFn: twoPlates.household });

  const eaters = householdQuery.data?.household?.eaters ?? [];

  /** Whose turn. The first person with unfinished answers, in household order. */
  const [eaterIndex, setEaterIndex] = useState(0);
  const [stage, setStage] = useState<Stage>("preset");
  const [cardIndex, setCardIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});

  const eater: Eater | undefined = eaters[eaterIndex];
  const groups = groupsQuery.data?.groups ?? [];
  const presets = groupsQuery.data?.presets ?? [];

  // Start each person from what they have already said, so a returning
  // half-finished profile picks up rather than starting over.
  useEffect(() => {
    if (!eater) return;
    setAnswers(eater.answers ?? {});
    setStage(Object.keys(eater.answers ?? {}).length > 0 ? "cards" : "preset");
    setCardIndex(0);
  }, [eater?.id]);

  const save = useMutation({
    mutationFn: (body: { affinities?: Record<string, number>; leftoverTolerance?: number }) =>
      twoPlates.saveTaste(eater!.id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["household"] }),
  });

  /** The first card this person has not answered — where a preset drops you. */
  const firstUnanswered = useMemo(
    () => Math.max(0, groups.findIndex((g) => answers[g.key] === undefined)),
    [groups, answers],
  );

  const answeredCount = groups.filter((g) => answers[g.key] !== undefined).length;

  if (groupsQuery.isLoading || householdQuery.isLoading) {
    return <Shell><p className="text-muted text-sm">Loading…</p></Shell>;
  }
  if (!eater) {
    return (
      <Shell>
        <p className="text-muted text-sm">No household yet.</p>
      </Shell>
    );
  }

  function applyPreset(preset: TastePreset | null) {
    if (!preset) {
      setStage("cards");
      setCardIndex(0);
      return;
    }
    const next = { ...preset.answers, ...answers };
    setAnswers(next);
    save.mutate({ affinities: preset.answers });
    setStage("cards");
    // Drop into the deck at the first thing the preset could not know.
    setCardIndex(groups.findIndex((g) => next[g.key] === undefined) ?? 0);
  }

  function answer(group: FoodGroup, score: number) {
    const next = { ...answers, [group.key]: score };
    setAnswers(next);
    save.mutate({ affinities: { [group.key]: score } });

    const remaining = groups.findIndex((g, i) => i > cardIndex && next[g.key] === undefined);
    if (remaining === -1) {
      const anyLeft = groups.findIndex((g) => next[g.key] === undefined);
      if (anyLeft === -1) setStage("leftovers");
      else setCardIndex(anyLeft);
    } else {
      setCardIndex(remaining);
    }
  }

  function finishEater(tolerance: number) {
    save.mutate(
      { leftoverTolerance: tolerance },
      {
        onSuccess: () => {
          if (eaterIndex + 1 < eaters.length) setEaterIndex(eaterIndex + 1);
          else navigate("/", { replace: true });
        },
      },
    );
  }

  const colour = plateColour(eaterIndex);

  return (
    <Shell>
      <header className="mb-6">
        <div className="flex items-center gap-2">
          <span aria-hidden className="h-3 w-3 rounded-full" style={{ background: colour }} />
          <p className="text-muted text-xs uppercase tracking-wide">
            {eater.name} — {eaterIndex + 1} of {eaters.length}
          </p>
        </div>
        {stage === "cards" && (
          <>
            <div
              className="bg-line mt-3 h-1.5 overflow-hidden rounded-full"
              role="progressbar"
              aria-valuenow={answeredCount}
              aria-valuemin={0}
              aria-valuemax={groups.length}
              aria-label="Foods answered"
            >
              <div
                className="h-full rounded-full transition-[width] duration-300"
                style={{ width: `${(answeredCount / groups.length) * 100}%`, background: colour }}
              />
            </div>
            <p className="text-muted mt-2 text-xs">
              {answeredCount} of {groups.length}
            </p>
          </>
        )}
      </header>

      {stage === "preset" && (
        <section>
          <h1 className="text-2xl font-semibold tracking-tight">
            Does one of these already describe {eater.name}?
          </h1>
          <p className="text-muted mt-2 text-sm">
            Start from it and correct what is wrong. Nothing here is a commitment — every
            card is still yours to change.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            {presets.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p)}
                className="bg-surface border-line rounded-xl border p-4 text-left"
              >
                <p className="font-semibold">{p.name}</p>
                <p className="text-muted mt-1 text-sm">{p.note}</p>
              </button>
            ))}
            <button
              onClick={() => applyPreset(null)}
              className="text-muted mt-1 self-start text-sm underline underline-offset-4"
            >
              None of these — ask me all sixteen
            </button>
          </div>
        </section>
      )}

      {stage === "cards" && groups[cardIndex] && (
        <section>
          <div className="bg-surface border-line rounded-2xl border p-6">
            <p className="text-muted text-xs uppercase tracking-wide">How about</p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight">
              {groups[cardIndex].name}
            </h1>
            {groups[cardIndex].examples && (
              <p className="text-muted mt-2 text-sm">{groups[cardIndex].examples}</p>
            )}
          </div>

          <div className="mt-5 flex flex-col gap-2.5">
            {SCORES.map((score) => {
              const chosen = answers[groups[cardIndex].key] === score;
              return (
                <button
                  key={score}
                  onClick={() => answer(groups[cardIndex], score)}
                  aria-pressed={chosen}
                  className="tap border-line flex items-center justify-between rounded-xl border px-5 text-left text-base font-medium"
                  style={chosen ? { borderColor: colour, background: `${colour}1a` } : undefined}
                >
                  {AFFINITY_LABELS[score]}
                  {score === 0 && (
                    <span className="text-muted text-xs font-normal">
                      only blocks a meal built on it
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => setCardIndex(Math.max(0, cardIndex - 1))}
              disabled={cardIndex === 0}
              className="text-muted text-sm underline underline-offset-4 disabled:opacity-40"
            >
              Back
            </button>
            {answeredCount === groups.length ? (
              <button
                onClick={() => setStage("leftovers")}
                className="text-sm underline underline-offset-4"
                style={{ color: colour }}
              >
                One last question
              </button>
            ) : (
              <button
                onClick={() => setCardIndex(firstUnanswered)}
                className="text-muted text-sm underline underline-offset-4"
              >
                Skip to unanswered
              </button>
            )}
          </div>
        </section>
      )}

      {stage === "leftovers" && (
        <section>
          <h1 className="text-2xl font-semibold tracking-tight">
            Will {eater.name} eat last night's dinner for lunch?
          </h1>
          <p className="text-muted mt-2 text-sm">
            Not a taste question. This decides how much gets cooked, not what.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            {[2, 1, 0].map((level) => (
              <button
                key={level}
                onClick={() => finishEater(level)}
                disabled={save.isPending}
                className="bg-surface border-line rounded-xl border p-4 text-left disabled:opacity-50"
              >
                <p className="font-semibold">{LEFTOVER_LABELS[level].title}</p>
                <p className="text-muted mt-1 text-sm">{LEFTOVER_LABELS[level].note}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      {save.isError && (
        <p role="alert" className="text-warn mt-6 text-sm">
          That answer did not save. Check your connection and tap it again.
        </p>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  // The app chrome owns the page padding now (#1323); this is only a grouping.
  return <>{children}</>;
}
